import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { api } from "../client";
import { qk } from "../keys";
import { isUnresolved } from "@/shared/lib/alertScope";
import type {
  Alert, AlertContext, AlertKind, AlertList, AlertLevel, AlertState, AlertSummary,
} from "../models";

export function useAlerts(params?: {
  site?: number;
  level?: AlertLevel;
  state?: AlertState;
  node?: number;
  /** 가스 경보 / 통신 이상(설비 무응답) — 백엔드 3.0.25~. */
  kind?: AlertKind;
  /** 이 시각 이후 발령(ISO) — 「오늘」「7일」 보기. 백엔드 3.0.25~. */
  raised_at__gte?: string;
  /** 서버 `page_size`(core/pagination: 기본 50, 상한 500). 생략하면 서버 기본값. */
  page_size?: number;
  /** 자유어 검색 — 서버 `search_fields`(가스·본문·함체코드·함체라벨·현장명). */
  search?: string;
  /** 서버 `ordering`(예: `-raised_at`, `level`). 생략하면 최신순. */
  ordering?: string;
}) {
  return useQuery({
    queryKey: qk.safety.alerts.list(params),
    queryFn: () =>
      api.get<AlertList>("/alerts/", { params }).then((r) => r.data),
    refetchInterval: 30_000,                       // refresh feed every 30s
  });
}

/** 미해제 질의 1건이 받아오는 최대 행 수 = 서버 `max_page_size`(core/pagination). */
export const UNRESOLVED_PAGE_SIZE = 500;

export interface UnresolvedAlerts {
  /** 미해제(`isUnresolved`) 경보, 최신순. 매 렌더 같은 배열 참조(useMemo). */
  alerts: Alert[];
  /** 세 질의가 모두 최초 1회 끝났다(성공이든 실패든). 기준선을 잡아도 되는 시점. */
  ready: boolean;
  /** 상태별 질의가 500행 상한에 걸렸다(응답에 `next` 가 있다). */
  truncated: boolean;
  /** 세 질의가 전부 실패했다 — 목록이 비어 있는 게 「경보 없음」이 아니다. */
  isError: boolean;
}

/**
 * 「아직 해제되지 않은 경보」 전체.
 *
 * <p>무필터 `GET /alerts/` 한 페이지는 <b>최신 50건</b>이다(CursorPagination page_size 50).
 * 발령마다 새 행이 생기고 쿨다운이 60초라 12개 지점이 동시에 울리면 분당 12행 —
 * 4분이면 50칸이 차고, <b>아직 해제되지 않은 오래된 경보가 1페이지 밖으로 밀려난다</b>.
 * 그래서 상태별로 좁혀 `page_size=500` 으로 따로 받는다: 해제(cleared)된 행이
 * 예산을 먹지 않으므로 상한이 「동시에 미해제인 경보 500건」이라는 훨씬 먼 벽이 된다.
 * `state` 는 exact 필터라(`filterset_fields`) open ∪ acknowledged 를 한 번에 못 받는다 —
 * 질의 2개가 백엔드 무변경 조건에서 유일한 길이다.</p>
 *
 * <p>세 번째 소스로 앱 전체가 이미 쓰고 있는 무필터 캐시(`useAlerts()`)를 합집합에 넣는다.
 * 같은 쿼리키라 요청은 늘지 않고, 서버 정렬이 `-raised_at` 이라 <b>새 경보는 항상 그 1페이지
 * 최상단</b>이다. 상태별 질의가 실패해도 「새로 터진 경보」는 계속 들어온다.</p>
 */
export function useUnresolvedAlerts(query?: {
  search?: string;
  ordering?: string;
}): UnresolvedAlerts {
  // 검색어·정렬은 **서버가** 건다. 받아 온 50/500건만 화면에서 걸러 내면 그 밖의
  // 미해제 경보는 검색해도 영영 안 나온다(2026-09-23). 질의가 없으면 인자를 아예
  // 넘기지 않아 기존 공유 캐시 키를 그대로 쓴다 — 종 배지가 추가 요청을 만들지 않는다.
  const extra = query?.search || query?.ordering ? query : undefined;
  const feed = useAlerts(extra);                              // 질의 없으면 앱 공유 캐시(추가 요청 0)
  const open = useAlerts({ ...extra, state: "open", page_size: UNRESOLVED_PAGE_SIZE });
  const ack = useAlerts({ ...extra, state: "acknowledged", page_size: UNRESOLVED_PAGE_SIZE });

  const alerts = useMemo(() => {
    const byId = new Map<number, Alert>();
    for (const page of [feed.data, open.data, ack.data]) {
      for (const a of page?.results ?? []) {
        if (a.id != null && isUnresolved(a)) byId.set(a.id, a);
      }
    }
    return [...byId.values()].sort(
      (a, b) => recencyKey(b) - recencyKey(a) || (b.id ?? 0) - (a.id ?? 0),
    );
  }, [feed.data, open.data, ack.data]);

  return {
    alerts,
    ready: !feed.isPending && !open.isPending && !ack.isPending,
    truncated: Boolean(open.data?.next) || Boolean(ack.data?.next),
    isError: feed.isError && open.isError && ack.isError,
  };
}

/**
 * 정렬 전용 시각 키. `raised_at` 이 없거나 파싱 불가면 <b>가장 최신</b>으로 본다 —
 * 시각이 깨졌다는 이유로 경보가 목록 뒤로 밀리면 안 된다(뒤로 밀리면 낭독 상한에서
 * 잘린다). 이 값은 정렬에만 쓰이고, 「읽을지 말지」 판정에는 어디서도 쓰지 않는다.
 */
function recencyKey(a: Alert): number {
  const t = a.raised_at ? Date.parse(a.raised_at) : NaN;
  return Number.isNaN(t) ? Number.MAX_SAFE_INTEGER : t;
}

export function useAcknowledgeAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note }: { id: number; note?: string }) =>
      api.post<Alert>(`/alerts/${id}/acknowledge/`, { note }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.safety.alerts.all }),
  });
}

export function useClearAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api.post<Alert>(`/alerts/${id}/clear/`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.safety.alerts.all }),
  });
}

/** Aggregate outcome of a bulk ack/clear over N alerts. */
export interface BulkAlertResult {
  ok: number;
  failed: number;
  total: number;
}

/**
 * Run `one(id)` for every id with bounded concurrency, **never rejecting**:
 * each request's success/failure is tallied so a partial failure — e.g. the
 * backend's `400 "already cleared"` on a raced acknowledge, or a network blip —
 * is reported rather than swallowed or aborting the rest of the batch.
 *
 * This replaces the old "fire N `mutate()` in a `forEach`" pattern, which was
 * fire-and-forget (errors vanished, no completion signal) and invalidated the
 * feed once *per* request (an N-fold refetch storm mid-operation).
 */
async function runBulk(
  ids: number[],
  one: (id: number) => Promise<unknown>,
  concurrency = 6,
): Promise<BulkAlertResult> {
  let ok = 0;
  let failed = 0;
  let cursor = 0;
  const worker = async () => {
    while (cursor < ids.length) {
      const id = ids[cursor++];
      try {
        await one(id);
        ok += 1;
      } catch {
        failed += 1;
      }
    }
  };
  const pool = Math.max(1, Math.min(concurrency, ids.length));
  await Promise.all(Array.from({ length: pool }, worker));
  return { ok, failed, total: ids.length };
}

/**
 * Bulk-acknowledge many alerts, settling every request and invalidating the
 * feed **once** when the whole batch is done. Returns aggregate counts so the
 * caller can toast partial failures. `isPending` drives the button's loading
 * state (and blocks a double-click from launching a second batch).
 */
export function useBulkAcknowledgeAlerts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) =>
      runBulk(ids, (id) =>
        api.post<Alert>(`/alerts/${id}/acknowledge/`, {}).then((r) => r.data),
      ),
    onSettled: () => qc.invalidateQueries({ queryKey: qk.safety.alerts.all }),
  });
}

/** Bulk-clear many alerts — see {@link useBulkAcknowledgeAlerts}. */
export function useBulkClearAlerts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) =>
      runBulk(ids, (id) =>
        api.post<Alert>(`/alerts/${id}/clear/`).then((r) => r.data),
      ),
    onSettled: () => qc.invalidateQueries({ queryKey: qk.safety.alerts.all }),
  });
}

/**
 * 경보 한눈에 — 진행 중·합계·시간/일/월 막대·통신 이상 사건(`GET /alerts/summary/`).
 * 서버가 아직 옛 버전(3.0.24 이하)이면 404 → `isError` 로 두고 화면은 그 칸만 숨긴다.
 */
export function useAlertSummary(site?: number | null) {
  const params = site ? { site } : undefined;
  return useQuery({
    queryKey: qk.safety.alerts.summary(params),
    queryFn: () => api.get<AlertSummary>("/alerts/summary/", { params }).then((r) => r.data),
    refetchInterval: 60_000,
    retry: (n, err: unknown) =>
      n < 2 && (err as { response?: { status?: number } })?.response?.status !== 404,
  });
}

/** 경보 1건의 발령 당시 측정값·위험도 기여·무응답 구간(`GET /alerts/{id}/context/`). */
export function useAlertContext(id: number | null | undefined) {
  return useQuery({
    queryKey: qk.safety.alerts.context(id ?? 0),
    queryFn: () => api.get<AlertContext>(`/alerts/${id}/context/`).then((r) => r.data),
    enabled: id != null && id > 0,
    staleTime: 60_000,
    retry: (n, err: unknown) =>
      n < 1 && (err as { response?: { status?: number } })?.response?.status !== 404,
  });
}

/** 경보 1건 — 상세 화면 직접 진입(푸시·링크)용. 목록 캐시에 있으면 그걸 먼저 보여 준다. */
export function useAlert(id: number | null | undefined) {
  return useQuery({
    queryKey: qk.safety.alerts.detail(id ?? 0),
    queryFn: () => api.get<Alert>(`/alerts/${id}/`).then((r) => r.data),
    enabled: id != null && id > 0,
    refetchInterval: 30_000,
  });
}
